import { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactElement, cloneElement, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export type IconButtonVariant = 'primary' | 'secondary' | 'destructive';

interface BaseIconButtonProps {
  variant?: IconButtonVariant;
  children: ReactElement<{ className?: string }>;
  iconSize?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
}

interface IconButtonAsButton extends BaseIconButtonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className' | 'aria-label' | 'disabled'> {
  as?: 'button';
  to?: never;
  'aria-label': string;
}

interface IconButtonAsLink extends BaseIconButtonProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'className' | 'href' | 'aria-label'> {
  as: 'link';
  to: string;
  'aria-label': string;
}

type IconButtonProps = IconButtonAsButton | IconButtonAsLink;

const iconSizes = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

export const IconButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, IconButtonProps>(
  ({ children, 'aria-label': ariaLabel, iconSize = 'md', as, to, className = '', disabled, ...props }, ref) => {
    const iconSizeClass = iconSizes[iconSize];

    // Clone children and apply icon size
    const iconWithSize = cloneElement(children, {
      className: `${iconSizeClass} ${children.props?.className || ''}`.trim(),
    });

    if (as === 'link' || to) {
      return (
        <Button
          asChild
          variant="outline"
          size="icon"
          className={className}
          disabled={disabled}
          aria-label={ariaLabel}
        >
          <Link
            to={to!}
            {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}
            ref={ref as React.Ref<HTMLAnchorElement>}
          >
            {iconWithSize}
          </Link>
        </Button>
      );
    }

    return (
      <Button
        variant="outline"
        size="icon"
        className={className}
        disabled={disabled}
        aria-label={ariaLabel}
        {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
        ref={ref as React.Ref<HTMLButtonElement>}
      >
        {iconWithSize}
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';